<script setup lang="ts">
import { computed } from "vue";
import Button from "./Button.vue";
import Icon from "./Icon.vue";
import IconButton from "./IconButton.vue";
import type { TagRow } from "../api/organization";
import { localized, t } from "../i18n";

/**
 * The company's tags over the conversation list, as WhatsApp Web's "Lists": each with its colour
 * and how many people it has. Whoever manages tags opens one to edit it; anyone else filters by it.
 */
const props = defineProps<{ tags: TagRow[]; canManage: boolean }>();
const emit = defineEmits<{
  close: [];
  create: [];
  edit: [tag: TagRow];
  filter: [tag: TagRow];
}>();

const active = computed(() => props.tags.filter((x) => x.isActive));
</script>
<template>
  <section class="side-panel" :aria-label="t('tags.title')">
    <header class="panel-header side-panel-header">
      <IconButton
        icon="close"
        :label="t('tags.panel.close')"
        @click="emit('close')"
      />
      <h2 class="grow">{{ t("tags.title") }}</h2>
      <IconButton
        v-if="canManage"
        icon="wa-plus"
        :label="t('tags.new')"
        @click="emit('create')"
      />
    </header>
    <div class="scroll side-panel-body">
      <button
        v-for="tag in active"
        :key="tag.id"
        type="button"
        class="tag-panel-row"
        @click="canManage ? emit('edit', tag) : emit('filter', tag)"
      >
        <i
          class="tag-panel-dot"
          :style="tag.color ? { '--tag': tag.color } : undefined"
        /><span class="grow">
          <span class="tag-panel-name truncate">{{ localized(tag.name) }}</span>
          <span class="tag-panel-sub">{{
            t("tags.panel.people", { count: tag.contacts })
          }}</span>
        </span>
      </button>
      <div v-if="!active.length" class="empty-compact">
        <Icon name="wa-lists" :size="40" />
        <p>{{ t("tags.panel.empty") }}</p>
        <span class="small muted">{{ t("tags.panel.empty_hint") }}</span>
        <Button v-if="canManage" icon="plus" @click="emit('create')">{{
          t("tags.new")
        }}</Button>
      </div>
    </div>
  </section>
</template>
